import _ from "lodash";
import { roiNumerator } from "lib/roi/strategies";

export const sortEstimationsByParams = (
  estimationsList,
  referenceEstimations,
  field,
  order,
  NewInvestment = false
) => {
  switch (field) {
    case "investment":
      return _.orderBy(
        estimationsList,
        (estimation) => {
          const matchingReferenceEstimation =
            referenceEstimations[
              `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
            ];
          const refPrice = NewInvestment
            ? matchingReferenceEstimation?.price_out_wo_taxes
            : matchingReferenceEstimation?.price;
          const estPrice = NewInvestment
            ? estimation.price_out_wo_taxes
            : estimation.price;
          return refPrice < estPrice
            ? 0
            : (refPrice - estPrice) * estimation.units;
        },
        order
      );
    case "investment_in":
      return _.orderBy(
        estimationsList,
        (estimation) => {
          const matchingReferenceEstimation =
            referenceEstimations[
              `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
            ];
          const refPriceIn = NewInvestment
            ? matchingReferenceEstimation?.price_in_wo_taxes
            : matchingReferenceEstimation?.price_in;
          const estPriceIn = NewInvestment
            ? estimation.price_in_wo_taxes
            : estimation.price_in;
          return refPriceIn < estPriceIn
            ? 0
            : (refPriceIn - estPriceIn) * estimation.units_in;
        },
        order
      );
    default:
      return _.orderBy(estimationsList, field, order);
  }
};

export const removeCorrectionToEstimation = (
  sourceEstimation,
  correctedEstimation,
  crossAdjustment
) => {
  const estimationUnitsWithoutCorrection =
    correctedEstimation.units -
    crossAdjustment.constant *
      (sourceEstimation !== undefined ? sourceEstimation.pct_price_cross : 0.0);

  const unitsRatio =
    estimationUnitsWithoutCorrection / correctedEstimation.units;

  return {
    ...correctedEstimation,
    units: estimationUnitsWithoutCorrection,
    units_in: unitsRatio * correctedEstimation.units_in,
    margin: unitsRatio * correctedEstimation.margin,
    sales_in:
      unitsRatio * correctedEstimation.units_in * correctedEstimation.price_in,
    sales: estimationUnitsWithoutCorrection * correctedEstimation.price,
    investment: unitsRatio * correctedEstimation.investment,
    investment_in: unitsRatio * correctedEstimation.investment_in,
  };
};

export const applyCorrectionToEstimation = (
  sourceEstimation,
  targetEstimation,
  crossAdjustment,
  isTradePromotionAnnual = false
) => {
  // Added a ternary statement to check if we have sourceEstimation. If we don't have data in sourceEstimation means that exist a combination of a elasticity by cycle which doen't have a scenario estimation combination probably is due to a an extra scope added in this cycle.
  const updatedEstimationUnits =
    targetEstimation.units +
    crossAdjustment.constant *
      (sourceEstimation !== undefined ? sourceEstimation.pct_price_cross : 0.0);

  const unitsRatio = updatedEstimationUnits / targetEstimation.units;
  // Cannibalisation disabled for pricing 2.0
  if (isTradePromotionAnnual) {
    return {
      ...targetEstimation,
      units: targetEstimation.units,
      units_in: targetEstimation.units_in,
      margin: targetEstimation.margin,
      sales_in: targetEstimation.units_in * targetEstimation.price_in,
      sales: targetEstimation.units * targetEstimation.price,
      investment: targetEstimation.investment,
      investment_in: targetEstimation.investment_in,
    };
  }
  return {
    ...targetEstimation,
    units: updatedEstimationUnits,
    units_in: unitsRatio * targetEstimation.units_in,
    margin: unitsRatio * targetEstimation.margin,
    sales_in:
      unitsRatio * targetEstimation.units_in * targetEstimation.price_in,
    sales: updatedEstimationUnits * targetEstimation.price,
    investment: unitsRatio * targetEstimation.investment,
    investment_in: unitsRatio * targetEstimation.investment_in,
  };
};

export const computeEstimationTotals = (
  estimationsList,
  referenceEstimationsList,
  NewInvestment = false,
  roiStrategy = "standard"
) => {
  let referenceEstimation;
  const totalsComputed = _.reduce(
    estimationsList,
    (totals, estimation) => {
      if (referenceEstimationsList) {
        referenceEstimation =
          referenceEstimationsList[
            `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
          ];
      }
      return {
        units: totals.units + estimation.units,
        units_in: totals.units_in + estimation.units_in,
        margin: totals.margin + estimation.margin,
        sales: totals.sales + estimation.sales,
        sales_in: totals.sales + estimation.sales_in,
        investment:
          totals.investment +
          ((NewInvestment
          ? referenceEstimation?.price_out_wo_taxes <=
            estimation.price_out_wo_taxes
          : referenceEstimation?.price <= estimation.price)
            ? 0
            : (NewInvestment
                ? referenceEstimation?.price_out_wo_taxes -
                  estimation.price_out_wo_taxes
                : referenceEstimation?.price - estimation.price) *
              estimation.units),
        investment_in:
          totals.investment_in +
          ((NewInvestment
          ? referenceEstimation?.price_in_wo_taxes <=
            estimation.price_in_wo_taxes
          : referenceEstimation?.price_in <= estimation.price_in)
            ? 0
            : (NewInvestment
                ? referenceEstimation?.price_in_wo_taxes -
                  estimation.price_in_wo_taxes
                : referenceEstimation?.price_in - estimation.price_in) *
              estimation.units_in),
        weightedPrice:
          totals.weightedPrice + estimation.price * estimation.units,
        referenceMargin:
          totals.referenceMargin +
          (referenceEstimation?.price <= estimation.price
            ? 0
            : referenceEstimation?.margin),
        marginROI:
          totals.marginROI +
          (referenceEstimation?.price <= estimation.price
            ? 0
            : estimation.margin),
        // Scenario and reference sold (sell-out) units, accumulated over the same promo rows
        // as the ROI numerator. Used by the incremental strategy.
        unitsROI:
          totals.unitsROI +
          (referenceEstimation?.price <= estimation.price
            ? 0
            : estimation.units),
        referenceUnits:
          totals.referenceUnits +
          (referenceEstimation?.price <= estimation.price
            ? 0
            : referenceEstimation?.units || 0),
      };
    },
    {
      units: 0,
      units_in: 0,
      margin: 0,
      sales: 0,
      sales_in: 0,
      investment_in: 0,
      investment: 0,
      price: 0,
      weightedPrice: 0,
      referenceMargin: 0,
      marginROI: 0,
      unitsROI: 0,
      referenceUnits: 0,
    }
  );
  // Commented because price is no longer the property we use to access wheighted price, instead we use weighted_avg_price_units
  // totalsComputed.price = _.round(
  //   totalsComputed.weightedPrice / totalsComputed.units,
  //   2
  // );
  totalsComputed.weighted_avg_price_units = _.round(
    totalsComputed.weightedPrice / totalsComputed.units,
    2
  );
  // ROI numerator (incremental margin) depends on the tenant's strategy.
  totalsComputed.roi =
    (roiNumerator(roiStrategy, {
      marginEsc: totalsComputed.marginROI,
      marginRef: totalsComputed.referenceMargin,
      unitsEsc: totalsComputed.unitsROI,
      unitsRef: totalsComputed.referenceUnits,
    }) /
      totalsComputed.investment_in) *
    100;

  return totalsComputed;
};

export const computeEstimationTotalsByDate = (
  estimationsList,
  referenceEstimationsList,
  NewInvestment = false,
  roiStrategy = "standard"
) => {
  let referenceEstimation;

  const totalsComputed = _.reduce(
    estimationsList,
    (totals, estimation) => {
      if (referenceEstimationsList)
        referenceEstimation =
          referenceEstimationsList[
            `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}-${estimation.date}`
          ];

      return {
        units: totals.units + estimation.units,
        units_in: totals.units_in + estimation.units_in,
        margin: totals.margin + estimation.margin,
        sales: totals.sales + estimation.sales,
        sales_in: totals.sales + estimation.sales_in,
        investment:
          totals.investment +
          ((NewInvestment
          ? referenceEstimation?.price_out_wo_taxes <=
            estimation.price_out_wo_taxes
          : referenceEstimation?.price <= estimation.price)
            ? 0
            : (NewInvestment
                ? referenceEstimation?.price_out_wo_taxes -
                  estimation.price_out_wo_taxes
                : referenceEstimation?.price - estimation.price) *
              estimation.units),
        investment_in:
          totals.investment_in +
          ((NewInvestment
          ? referenceEstimation?.price_in_wo_taxes <=
            estimation.price_in_wo_taxes
          : referenceEstimation?.price_in <= estimation.price_in)
            ? 0
            : (NewInvestment
                ? referenceEstimation?.price_in_wo_taxes -
                  estimation.price_in_wo_taxes
                : referenceEstimation?.price_in - estimation.price_in) *
              estimation.units_in),
        referenceMargin:
          referenceEstimation?.price <= estimation.price
            ? 0
            : referenceEstimation?.margin,
        marginROI:
          totals.marginROI +
          (referenceEstimation?.price <= estimation.price
            ? 0
            : estimation.margin),
        unitsROI:
          totals.unitsROI +
          (referenceEstimation?.price <= estimation.price
            ? 0
            : estimation.units),
        referenceUnits:
          totals.referenceUnits +
          (referenceEstimation?.price <= estimation.price
            ? 0
            : referenceEstimation?.units || 0),
      };
    },
    {
      units: 0,
      units_in: 0,
      margin: 0,
      sales: 0,
      sales_in: 0,
      investment_in: 0,
      investment: 0,
      referenceMargin: 0,
      marginROI: 0,
      unitsROI: 0,
      referenceUnits: 0,
    }
  );
  // ROI numerator (incremental margin) depends on the tenant's strategy.
  totalsComputed.roi =
    (roiNumerator(roiStrategy, {
      marginEsc: totalsComputed.marginROI,
      marginRef: totalsComputed.referenceMargin,
      unitsEsc: totalsComputed.unitsROI,
      unitsRef: totalsComputed.referenceUnits,
    }) /
      totalsComputed.investment_in) *
    100;

  return totalsComputed;
};

export const groupEstimationsByDate = (
  estimationsGrouped,
  metadata,
  referenceList,
  NewInvestment = false
) => {
  const estimationReduced = {};
  _.forEach(
    estimationsGrouped,
    (estimationsGroupValues, estimationsGroupKeys) => {
      let referenceEstimation;
      estimationReduced[estimationsGroupKeys] = {
        retailer_id: estimationsGroupValues[0].retailer_id,
        retailer: estimationsGroupValues[0].retailer,
        retailerGroup: estimationsGroupValues[0].retailer_group,
        retailer_group_id: estimationsGroupValues[0].retailer_group
          ? estimationsGroupValues[0].retailer.retailer_group_id
          : null,
        product: estimationsGroupValues[0].product,
        area: estimationsGroupValues[0].area,
        area_id: estimationsGroupValues[0].area_id,
        product_id: estimationsGroupValues[0].product_id,
        price:
          metadata.price[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroupValues, (estimation) => {
                  return estimation.price * estimation.units;
                }) / _.sumBy(estimationsGroupValues, `units`),
                metadata.price[0].decimals
              )
            : null,
        price_in:
          metadata.price_in[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroupValues, (estimation) => {
                  return estimation.price_in * estimation.units_in;
                }) / _.sumBy(estimationsGroupValues, `units_in`),
                metadata.price_in[0].decimals
              )
            : null,
        price_out_wo_taxes:
          metadata.price[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroupValues, (estimation) => {
                  return estimation.price_out_wo_taxes * estimation.units;
                }) / _.sumBy(estimationsGroupValues, `units`),
                metadata.price[0].decimals
              )
            : null,
        price_in_wo_taxes:
          metadata.price_in[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroupValues, (estimation) => {
                  return estimation.price_in_wo_taxes * estimation.units_in;
                }) / _.sumBy(estimationsGroupValues, `units_in`),
                metadata.price_in[0].decimals
              )
            : null,
        cost: _.meanBy(estimationsGroupValues, (estimation) => {
          return estimation.cost;
        }),
        units:
          metadata.units[0].visibility_type !== 0
            ? _.sumBy(estimationsGroupValues, `units`)
            : null,
        units_in:
          metadata.units_in[0].visibility_type !== 0
            ? _.sumBy(estimationsGroupValues, `units_in`)
            : null,
        margin:
          metadata.margin[0].visibility_type !== 0
            ? _.sumBy(estimationsGroupValues, `margin`)
            : null,
        sales:
          metadata.sales[0].visibility_type !== 0
            ? _.sumBy(estimationsGroupValues, `sales`)
            : null,
        sales_in:
          metadata.sales_in[0].visibility_type !== 0
            ? _.sumBy(estimationsGroupValues, `sales_in`)
            : null,
        investment:
          metadata.investment[0].visibility_type !== 0 && referenceList
            ? _.reduce(
                estimationsGroupValues,
                (total, estimation) => {
                  if (referenceList)
                    referenceEstimation =
                      referenceList[
                        `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
                      ];
                  const refPrice = NewInvestment
                    ? referenceEstimation?.price_out_wo_taxes
                    : referenceEstimation?.price;
                  const estPrice = NewInvestment
                    ? estimation.price_out_wo_taxes
                    : estimation.price;
                  return (
                    total +
                    (refPrice < estPrice
                      ? 0
                      : (refPrice - estPrice) * estimation.units)
                  );
                },
                0
              )
            : null,
        investment_in:
          metadata.investment_in[0].visibility_type !== 0 && referenceList
            ? _.reduce(
                estimationsGroupValues,
                (total, estimation) => {
                  if (referenceList)
                    referenceEstimation =
                      referenceList[
                        `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
                      ];
                  const refPriceIn = NewInvestment
                    ? referenceEstimation?.price_in_wo_taxes
                    : referenceEstimation?.price_in;
                  const estPriceIn = NewInvestment
                    ? estimation.price_in_wo_taxes
                    : estimation.price_in;
                  return (
                    total +
                    (refPriceIn < estPriceIn
                      ? 0
                      : (refPriceIn - estPriceIn) * estimation.units_in)
                  );
                },
                0
              )
            : null,
        pct_price_cross: _.meanBy(estimationsGroupValues, (estimation) => {
          return estimation.pct_price_cross;
        }),
      };
    }
  );
  return estimationReduced;
};

export const groupEstimationsDaily = (
  estimationsGrouped,
  metadata,
  referenceList,
  NewInvestment = false
) => {
  const estimationReduced = {};
  _.forEach(
    estimationsGrouped,
    (estimationsGroupValues, estimationsGroupKeys) => {
      let referenceEstimation;
      estimationReduced[estimationsGroupKeys] = {
        // We just take the first element of the array because area, retailerGroup, retailer and product will be the same in this group
        retailer_id: estimationsGroupValues[0].retailer_id,
        retailer: estimationsGroupValues[0].retailer,
        retailerGroup: estimationsGroupValues[0].retailer_group,
        retailer_group_id: estimationsGroupValues[0].retailer_group
          ? estimationsGroupValues[0].retailer.retailer_group_id
          : null,
        product: estimationsGroupValues[0].product,
        area: estimationsGroupValues[0].area,
        area_id: estimationsGroupValues[0].area_id,
        product_id: estimationsGroupValues[0].product_id,
        price:
          metadata.price[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroupValues, (estimation) => {
                  return estimation.price * estimation.units;
                }) / _.sumBy(estimationsGroupValues, `units`),
                metadata.price[0].decimals
              )
            : null,
        price_in:
          metadata.price_in[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroupValues, (estimation) => {
                  return estimation.price_in * estimation.units_in;
                }) / _.sumBy(estimationsGroupValues, `units_in`),
                metadata.price_in[0].decimals
              )
            : null,
        price_out_wo_taxes:
          metadata.price_in[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroupValues, (estimation) => {
                  return estimation.price_out_wo_taxes * estimation.units;
                }) / _.sumBy(estimationsGroupValues, `units`),
                metadata.price[0].decimals
              )
            : null,
        price_in_wo_taxes:
          metadata.price_in[0].visibility_type !== 0
            ? _.round(
                _.sumBy(estimationsGroupValues, (estimation) => {
                  return estimation.price_in_wo_taxes * estimation.units_in;
                }) / _.sumBy(estimationsGroupValues, `units_in`),
                metadata.price_in[0].decimals
              )
            : null,
        cost: _.meanBy(estimationsGroupValues, (estimation) => {
          return estimation.cost;
        }),
        units:
          metadata.units[0].visibility_type !== 0
            ? _.sumBy(estimationsGroupValues, `units`)
            : null,
        units_in:
          metadata.units_in[0].visibility_type !== 0
            ? _.sumBy(estimationsGroupValues, `units_in`)
            : null,
        margin:
          metadata.margin[0].visibility_type !== 0
            ? _.sumBy(estimationsGroupValues, `margin`)
            : null,
        sales:
          metadata.sales[0].visibility_type !== 0
            ? _.sumBy(estimationsGroupValues, `sales`)
            : null,
        sales_in:
          metadata.sales_in[0].visibility_type !== 0
            ? _.sumBy(estimationsGroupValues, `sales_in`)
            : null,
        investment:
          metadata.investment[0].visibility_type !== 0 && referenceList
            ? _.reduce(
                estimationsGroupValues,
                (total, estimation) => {
                  if (referenceList)
                    referenceEstimation =
                      referenceList[
                        `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
                      ];
                  const refPrice = NewInvestment
                    ? referenceEstimation?.price_out_wo_taxes
                    : referenceEstimation?.price;
                  const estPrice = NewInvestment
                    ? estimation.price_out_wo_taxes
                    : estimation.price;
                  return (
                    total +
                    (refPrice < estPrice
                      ? 0
                      : (refPrice - estPrice) * estimation.units)
                  );
                },
                0
              )
            : null,
        investment_in:
          metadata.investment_in[0].visibility_type !== 0 && referenceList
            ? _.reduce(
                estimationsGroupValues,
                (total, estimation) => {
                  if (referenceList)
                    referenceEstimation =
                      referenceList[
                        `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
                      ];
                  const refPriceIn = NewInvestment
                    ? referenceEstimation?.price_in_wo_taxes
                    : referenceEstimation?.price_in;
                  const estPriceIn = NewInvestment
                    ? estimation.price_in_wo_taxes
                    : estimation.price_in;
                  return (
                    total +
                    (refPriceIn < estPriceIn
                      ? 0
                      : (refPriceIn - estPriceIn) * estimation.units_in)
                  );
                },
                0
              )
            : null,
        pct_price_cross: _.meanBy(estimationsGroupValues, (estimation) => {
          return estimation.pct_price_cross;
        }),
      };
    }
  );
  return estimationReduced;
};

export const getCorrespondingReferenceEstimation = (
  estimation,
  referenceEstimationsList
) => {
  return referenceEstimationsList[
    `${estimation.product_id}-${estimation.area_id}-${estimation.retailer_id}`
  ];
};

export const getEstimationElasticity = (units, price, refUnits, refPrice) => {
  return price === refPrice
    ? 0
    : _.round(
        (units - refUnits) / refUnits / ((price - refPrice) / refPrice),
        2
      );
};
